import { ReactNode, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { I18nContext, translations, type Lang } from "./i18n-context";

export { useI18n } from "./i18n-context";
export type { Lang } from "./i18n-context";

/** NL path (without /en prefix) <-> EN path pairs. */
const PATH_PAIRS: { nl: RegExp; en: RegExp; toEn: string; toNl: string }[] = [
  { nl: /^\/woordenlijst$/, en: /^\/glossary$/, toEn: "/glossary", toNl: "/woordenlijst" },
  { nl: /^\/diensten\/(.+)$/, en: /^\/services\/(.+)$/, toEn: "/services/$1", toNl: "/diensten/$1" },
];

const toEnPath = (nlPath: string) => {
  for (const p of PATH_PAIRS) {
    if (p.nl.test(nlPath)) return nlPath.replace(p.nl, p.toEn);
  }
  return nlPath;
};

const toNlPath = (enPath: string) => {
  for (const p of PATH_PAIRS) {
    if (p.en.test(enPath)) return enPath.replace(p.en, p.toNl);
  }
  return enPath;
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const lang: Lang =
    location.pathname === "/en" || location.pathname.startsWith("/en/") || location.pathname === "/glossary"
      ? "en"
      : "nl";

  const setLang = useCallback(
    (next: Lang) => {
      const isEn = location.pathname === "/en" || location.pathname.startsWith("/en/");
      const bare = isEn ? location.pathname.replace(/^\/en/, "") || "/" : location.pathname;

      let target: string;
      if (next === "en") {
        const enPath = toEnPath(bare);
        target = enPath === "/" ? "/en" : `/en${enPath}`;
      } else {
        target = toNlPath(bare) || "/";
      }

      navigate(target + location.search + location.hash);
    },
    [location, navigate]
  );

  const value = useMemo(() => ({ lang, setLang, t: translations }), [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
